/**
 * Pure Live-card derivation from the annotated preview: what a container is
 * ACTUALLY doing today. Honest states only — "running" needs runtime rows,
 * "done" needs every live drop sent; a day that was never generated says
 * so instead of pretending.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanContainerRow, DayPlanDropRow } from '../../drop-generator.types';

export interface ContainerLiveFacts {
  /** Drops still owned by today's plan (tombstoned ones are out). */
  planned: number;
  sent: number;
  /** Phase 9 — of the sent ones, how many the engine sent (auto-send). */
  auto: number;
  staged: number;
  scheduled?: number;
  /** Slots the operator skipped by hand today (tombstoned, not stopped). */
  skippedToday: number;
  /** Drops a verdict stopped today (failMode STOP_FOR_DAY) — slice 3. */
  stoppedToday: number;
  /** Every group's pool is exhausted — nothing plans until a Reset pool. */
  paused: boolean;
  state: 'not-generated' | 'waiting' | 'running' | 'scheduled' | 'done' | 'stopped' | 'paused';
  /** Last send instant (ISO) — the "done HH:MM" stamp. */
  doneAt: string | null;
}

const live = (drop: DayPlanDropRow) => !drop.runtime?.tombstoned;
const stopped = (drop: DayPlanDropRow) => Boolean(drop.runtime?.tombstoned) && Boolean(drop.runtime?.skipReason?.startsWith('stopped'));

export function deriveContainerLive(container: DayPlanContainerRow): ContainerLiveFacts {
  const drops = container.drops;
  const activeDrops = drops.filter(live);
  const sentDrops = activeDrops.filter((d) => d.runtime?.sentAt);
  const scheduled = activeDrops.filter((drop) => drop.runtime?.remoteScheduledAt && !drop.runtime.sentAt).length;
  const hasRuntime = drops.some((d) => d.runtime);
  // An all-skipped day is DONE (0/0) — the footer explains the skips.
  const done = hasRuntime && sentDrops.length === activeDrops.length;
  const doneAt = sentDrops.reduce<string | null>(
    (max, d) => (max === null || (d.runtime!.sentAt as string) > max ? (d.runtime!.sentAt as string) : max),
    null,
  );
  const stoppedToday = drops.filter(stopped).length;
  const paused = container.groups.length > 0 && container.groups.every((g) => g.paused);
  return {
    planned: activeDrops.length,
    sent: sentDrops.length,
    auto: sentDrops.filter((d) => d.runtime?.sentBy === 'ENGINE').length,
    staged: activeDrops.filter((d) => d.runtime?.staged).length,
    scheduled,
    skippedToday: drops.length - activeDrops.length - stoppedToday,
    stoppedToday,
    paused,
    state: paused
      ? 'paused'
      : !hasRuntime
        ? 'not-generated'
        : stoppedToday > 0
          ? 'stopped'
          : done
            ? 'done'
            : sentDrops.length > 0
              ? 'running'
              : scheduled > 0
                ? 'scheduled'
                : 'waiting',
    doneAt: done ? doneAt : null,
  };
}

/** Phase 9 — unsent drafts auto-send holds or left for a person, with why. */
export function heldDrops(container: DayPlanContainerRow): Array<{ atUtc: string; note: string }> {
  return container.drops
    .filter((d) => live(d) && !d.runtime?.sentAt && d.runtime?.autoSendNote)
    .map((d) => ({ atUtc: d.atUtc, note: d.runtime!.autoSendNote as string }));
}

/** Per-group sent/planned counts for the card's "Today" row. */
export function groupCounts(container: DayPlanContainerRow, listGroupId: string): { sent: number; planned: number } {
  const drops = container.drops.filter((d) => d.groupId === listGroupId && live(d));
  return {
    sent: drops.filter((d) => d.runtime?.sentAt).length,
    planned: drops.length,
  };
}

/** Drafts the generator still holds for a group — what a DEV simulation
 *  can fake (sent, tombstoned, swept-away rows are out). */
export function groupUnsentDrafts(container: DayPlanContainerRow, listGroupId: string): number {
  return container.drops.filter((d) => d.groupId === listGroupId && live(d) && Boolean(d.runtime?.hasDraft) && !d.runtime?.sentAt).length;
}

/** 19/10/2026 — one list's drops of a group with several lists today. */
export function listCounts(
  container: DayPlanContainerRow,
  listGroupId: string,
  wiserId: number,
): { sent: number; planned: number } {
  const drops = container.drops.filter((d) => d.groupId === listGroupId && d.listWiserId === wiserId && live(d));
  return { sent: drops.filter((d) => d.runtime?.sentAt).length, planned: drops.length };
}
